import { useEffect, useMemo, useRef } from "react";
import type { EntryConditions, HistoryPoint } from "../types";
import {
  extractSignalEvents,
  normalizeSide,
  timeframeReadouts,
} from "../lib/signals";
import { formatAgo } from "../lib/format";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { cn } from "@/lib/utils";

interface Props {
  data: EntryConditions | null;
  history: HistoryPoint[] | null;
  now: number;
  audioEnabled: boolean;
}

/**
 * Banner that lights up when initial_trade or recovery_trade fires.
 * Also surfaces a "pre-signal" state when the raw RSI/BB conditions agree on a
 * given side but the server hasn't emitted the trade yet — useful because the
 * server waits for a confirmation bar before setting initial_trade/recovery_trade.
 */
export function SignalBanner({ data, history, now, audioEnabled }: Props) {
  const initial = normalizeSide(data?.initial_trade ?? null);
  const recovery = normalizeSide(data?.recovery_trade ?? null);

  const preInitial = usePreSignal(data, "1m-only");
  const preRecovery = usePreSignal(data, "all-tf");

  const lastEvent = useMemo(() => {
    if (!history) return null;
    const evs = extractSignalEvents(history);
    return evs.length ? evs[evs.length - 1] : null;
  }, [history]);

  const alertKey = useMemo(() => {
    if (recovery) return `recovery:${recovery}`;
    if (initial) return `initial:${initial}`;
    return null;
  }, [initial, recovery]);

  useBeepOnChange(alertKey, audioEnabled);

  const activeRow = recovery
    ? {
        label: "RECOVERY",
        side: recovery,
        subtitle: "All 4 timeframes agree",
      }
    : initial
    ? {
        label: "INITIAL",
        side: initial,
        subtitle: "1m signal confirmed",
      }
    : null;

  if (activeRow) {
    const sideIsBuy = activeRow.side === "BUY";
    return (
      <Card
        className={cn(
          "flex items-center gap-3 px-4 py-2.5 border-2",
          sideIsBuy
            ? "border-buy/50 bg-gradient-to-r from-buy/20 to-buy/5"
            : "border-sell/50 bg-gradient-to-r from-sell/20 to-sell/5"
        )}
      >
        <Badge variant={sideIsBuy ? "buy" : "sell"} className="font-mono-tabular tracking-widest">
          {activeRow.label}
        </Badge>
        <span
          className={cn(
            "font-mono-tabular text-lg font-bold leading-none",
            sideIsBuy ? "text-buy" : "text-sell"
          )}
        >
          {activeRow.side}
        </span>
        <span className="text-xs text-muted-foreground">{activeRow.subtitle}</span>
      </Card>
    );
  }

  return (
    <Card className="flex flex-wrap items-center gap-3 px-4 py-2.5">
      <Badge variant="outline" className="font-mono-tabular tracking-widest text-muted-foreground">
        NO SIGNAL
      </Badge>
      <PreSignalNote preRecovery={preRecovery} preInitial={preInitial} />
      {lastEvent && (
        <span className="ml-auto text-xs text-muted-foreground">
          Last: {formatAgo(lastEvent.time, now)} ·{" "}
          <span className="font-mono-tabular uppercase tracking-wider">
            {lastEvent.kind === "recovery" ? "R" : "I"}
          </span>
          ·
          <span
            className={cn(
              "font-mono-tabular font-semibold",
              lastEvent.side === "BUY" ? "text-buy" : "text-sell"
            )}
          >
            {" "}{lastEvent.side}
          </span>
        </span>
      )}
    </Card>
  );
}

function PreSignalNote({
  preRecovery,
  preInitial,
}: {
  preRecovery: "BUY" | "SELL" | null;
  preInitial: "BUY" | "SELL" | null;
}) {
  if (preRecovery) {
    return (
      <span className="text-xs text-muted-foreground">
        Conditions agree for{" "}
        <span
          className={cn(
            "font-mono-tabular font-semibold",
            preRecovery === "BUY" ? "text-buy" : "text-sell"
          )}
        >
          {preRecovery}
        </span>{" "}
        on all 4 timeframes — awaiting confirmation bar
      </span>
    );
  }
  if (preInitial) {
    return (
      <span className="text-xs text-muted-foreground">
        1m conditions agree for{" "}
        <span
          className={cn(
            "font-mono-tabular font-semibold",
            preInitial === "BUY" ? "text-buy" : "text-sell"
          )}
        >
          {preInitial}
        </span>{" "}
        — awaiting confirmation bar
      </span>
    );
  }
  return <span className="text-xs text-muted-foreground">Watching…</span>;
}

function usePreSignal(
  data: EntryConditions | null,
  mode: "1m-only" | "all-tf"
): "BUY" | "SELL" | null {
  return useMemo(() => {
    if (!data) return null;
    const rows = timeframeReadouts(data);
    const relevant = mode === "1m-only" ? rows.filter((r) => r.timeframe === "1m") : rows;
    if (relevant.every((r) => r.buyAgrees)) return "BUY";
    if (relevant.every((r) => r.sellAgrees)) return "SELL";
    return null;
  }, [data, mode]);
}

function useBeepOnChange(key: string | null, enabled: boolean) {
  const prev = useRef<string | null>(null);
  useEffect(() => {
    if (!enabled) {
      prev.current = key;
      return;
    }
    if (key && key !== prev.current) {
      beep();
    }
    prev.current = key;
  }, [key, enabled]);
}

let audioCtx: AudioContext | null = null;
function beep() {
  try {
    if (!audioCtx) {
      const Ctor = (window.AudioContext ||
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext);
      if (!Ctor) return;
      audioCtx = new Ctor();
    }
    const ctx = audioCtx;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.2, ctx.currentTime + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.35);
    osc.connect(gain).connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.4);
  } catch {
    // Audio is best-effort; ignore failures.
  }
}
